import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { FiberCocModelsTable } from '../-components/FiberCocModelsTable';
import { fiberCocModelsQueryOptions } from '../-hooks/useAdminFiberCocModels';
import { useFiberCocModelsPage } from '../-hooks/useFiberCocModelsPage';
import { MAX_COUNT } from '../-utils/listParams';

const fiberCocModelsSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  /** The footer offers 50 / 100 / 500 and the route caps `count` at 500. */
  limit: fallback(
    z
      .number()
      .int()
      .transform(v => Math.min(Math.max(v, 10), MAX_COUNT)),
    50,
  ),
  /**
   * `field:ASC` / `field:DESC`. Only `name` and `updatedAt` survive
   * `toFiberCocModelsParams` — anything else is dropped there rather than
   * sent, because the route's sort pattern is anchored and would 400.
   */
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
  // No `search`: the route has no free-text parameter — `name` is a key inside
  // the `filter` JSON blob, which is the table's column filter.
});

export const Route = createFileRoute('/management/fiber-coc-models/(pages)/')({
  validateSearch: fiberCocModelsSearch,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
  }),
  // Swallowed on purpose: the loader is a prefetch, and letting it reject hands
  // the route to the router's error boundary, which replaces the page and hides
  // the table's own "could not load" banner. The component's `useQuery` hits
  // the same failure and surfaces it inline.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(fiberCocModelsQueryOptions(deps))
      .catch(() => undefined),
  component: FiberCocModelsPage,
});

function FiberCocModelsPage() {
  const page = useFiberCocModelsPage();

  return (
    <PageContainer>
      <FiberCocModelsTable page={page} />
    </PageContainer>
  );
}
